import type { Owner, PlanId } from '@/types/owner';

/**
 * Accès & essai gratuit.
 *
 * À l'inscription, l'owner reçoit un essai « freemium » de TRIAL_DAYS jours
 * avec les accès du plan Business. L'expiration est purement temporelle
 * (trialEndsAt) : aucun cron requis, tout est recalculé à la volée.
 * Une fois l'essai expiré sans abonnement, l'owner se comporte exactement
 * comme s'il n'avait aucun plan actif.
 */
export const TRIAL_DAYS = 3;
export const TRIAL_PLAN: PlanId = 'business';
const DAY_MS = 24 * 60 * 60 * 1000;

export function trialEndFrom(now: number = Date.now()): number {
  return now + TRIAL_DAYS * DAY_MS;
}

export function isTrialing(owner?: Owner | null): boolean {
  return owner?.subscriptionStatus === 'trialing';
}

export function isTrialActive(owner?: Owner | null, now: number = Date.now()): boolean {
  return isTrialing(owner) && typeof owner?.trialEndsAt === 'number' && owner.trialEndsAt > now;
}

export function isTrialExpired(owner?: Owner | null, now: number = Date.now()): boolean {
  return isTrialing(owner) && (!owner?.trialEndsAt || owner.trialEndsAt <= now);
}

/** Vrai si l'owner peut travailler (abonnement payé OU essai en cours). */
export function hasActiveAccess(owner?: Owner | null, now: number = Date.now()): boolean {
  if (!owner) return false;
  if (owner.subscriptionStatus === 'active') return true;
  return isTrialActive(owner, now);
}

/** Plan réellement applicable (limites). Null si l'accès est perdu. */
export function effectivePlan(owner?: Owner | null, now: number = Date.now()): PlanId | null {
  if (!owner) return null;
  return hasActiveAccess(owner, now) ? owner.plan ?? null : null;
}

/** Jours restants d'essai (arrondi au supérieur), 0 si pas d'essai actif. */
export function trialDaysLeft(owner?: Owner | null, now: number = Date.now()): number {
  if (!isTrialActive(owner, now) || !owner?.trialEndsAt) return 0;
  return Math.max(1, Math.ceil((owner.trialEndsAt - now) / DAY_MS));
}
